import { useState, useEffect, useRef } from 'react';
import { callDashboardRoutesBatch } from '../../../api/dashboardV2Api';

const EMPTY = { value: 0, trend: { direction: null, delta: '' }, subLabel: '' };

export function useKPIData(activeIds, filters) {
  const [data, setData] = useState(() =>
    Object.fromEntries(activeIds.map(id => [id, EMPTY]))
  );
  const [loading, setLoading] = useState({});

  const filtersKey = JSON.stringify(filters);
  const idsKey = JSON.stringify(activeIds);
  const prevKeys = useRef({ filtersKey: null, idsKey: null });
  const prevIds = useRef([]);

  useEffect(() => {
    // Seed empty data for any newly-active IDs immediately
    setData(prev => {
      const next = { ...prev };
      activeIds.forEach(id => { if (!next[id]) next[id] = EMPTY; });
      return next;
    });

    if (activeIds.length === 0) return;

    const filtersChanged = prevKeys.current.filtersKey !== filtersKey;
    const idsChanged = prevKeys.current.idsKey !== idsKey;

    if (!filtersChanged && !idsChanged) return;

    // Filters changed → refetch everything (cached data is stale)
    // Only IDs changed → fetch only the newly added IDs
    const toFetch = filtersChanged
      ? activeIds
      : activeIds.filter(id => !prevIds.current.includes(id));

    prevKeys.current = { filtersKey, idsKey };
    prevIds.current = activeIds;

    if (toFetch.length === 0) return;

    setLoading(prev => ({
      ...prev,
      ...Object.fromEntries(toFetch.map(id => [id, true])),
    }));

    // One batch request for all KPI IDs
    callDashboardRoutesBatch(toFetch, filters).then(res => {
      const dataUpdates = {};
      const loadUpdates = {};
      toFetch.forEach(id => {
        const kpiData = res?.data?.kpis?.[id];
        if (res?.success && kpiData) {
          dataUpdates[id] = {
            value:    kpiData.value,
            trend:    kpiData.trend ?? { direction: null, delta: '' },
            subLabel: kpiData.subLabel ?? '',
          };
        }
        loadUpdates[id] = false;
      });
      setData(prev => ({ ...prev, ...dataUpdates }));
      setLoading(prev => ({ ...prev, ...loadUpdates }));
    }).catch(() => {
      // On error, clear loading spinners
      setLoading(prev => ({
        ...prev,
        ...Object.fromEntries(toFetch.map(id => [id, false])),
      }));
    });
  }, [idsKey, filtersKey]);

  return { data, loading };
}
